/************************************** THIRD-PARTY MODULES ***************************************/
import React from 'react';
import cn from 'classnames';
import {inject} from 'mobx-react';

/**************************************** PROJECT MODULES *****************************************/
import {StoreProps, translate} from '@canimmunize/core-imports';
import {isNullOrUndefined} from 'mad-utils/lib/shared';
import moment from 'moment';

/**************************************** TYPE DEFINITIONS ****************************************/
interface FooterProps extends StoreProps {
    isFixed?: boolean;
    marginRight?: number | string;
    outerClassName?: string;
}

/********************************************* CONFIG *********************************************/
const textCssClasses = `fs-12 helvetica-neue near-light-gray`;

/******************************************** HELPERS *********************************************/
/**
 * @param {string} envName Name of current environment
 * @return {boolean} true if envName identifies a production environment
 */
const isProdEnv = (envName: string): boolean =>
    envName.toLowerCase() === 'production' || envName.toLowerCase() === 'prod';

/**
 * Build version string for display in footer
 * @param {string} appVersion Numeric version of the app
 * @param {string} buildNumber Build number, set by environment variable
 * @param {string} envName Environment name - development, uat, or production (dev, uat, or prod)
 * @return {string} Constructed version string for display in footer
 */
const buildVersionString = (appVersion: string, buildNumber: string, envName: string): string =>
    `v${appVersion}${isProdEnv(envName) ? `` : `.${buildNumber}`}${
        isProdEnv(envName) ? '' : '-' + envName.truncate(3, '').toLowerCase()
    }`;

/********************************************* EXPORT *********************************************/
/**
 * Footer of page
 * Contains copyright, version, environment, build number
 */
export const Footer = inject('appState')(
    ({appState, isFixed, marginRight, outerClassName}: FooterProps) => {
        const {APP_VERSION, BUILD_NUMBER, RELEASE_ENV} = appState.envVarStore;
        const versionString = buildVersionString(APP_VERSION, BUILD_NUMBER, RELEASE_ENV);

        const year = moment()
            .year()
            .toString();

        const copyright = translate(`settings_copyright`).replace(`%d`, year);

        return (
            <footer
                role="contentinfo"
                className={cn(
                    outerClassName,
                    `flex end`,
                    `zi-0`,
                    isFixed && `fixed right-0 bottom-0`
                )}
            >
                <div
                    className={cn(`flex row space-between`, `mb-10`)}
                    style={{
                        marginRight: !isNullOrUndefined(marginRight) ? marginRight : 30,
                    }}
                >
                    <div className={cn(textCssClasses, `mr-20 ml-10`)}>{versionString}</div>
                    <div className={cn(textCssClasses)}>{copyright}</div>
                </div>
            </footer>
        );
    }
);

Footer.displayName = `Footer`;
